import _ from 'underscore';
import Cookie from '@component/common/src/util/Cookie';
import UserStatus from '@component/common/src/util/UserStatus';
import AnalyticsQueryString from '@component/common/src/util/AnalyticsQueryString';
import { Cookies } from '../util/Constants';
import CookieUtil from './CookieUtil';

export default class AttributesHelper {
  constructor(options) {
    Object.assign(this, options);
  }

  // Utilities
  getUrlParam(param) {
    if (this.queryMap) {
      return this.queryMap[param];
    }
    this.queryMap = AnalyticsQueryString.buildQueryMap();
    return this.queryMap[param];
  }

  getFnParamVal(key) {
    if (this.fnParamMap) {
      return this.fnParamMap[key];
    }

    let fnParamsUrl = window.location.href.split('#fn=')[1];

    if (!fnParamsUrl) {
      this.fnParamMap = {};
      return null;
    }

    fnParamsUrl = decodeURIComponent(fnParamsUrl.replace(/[&=]*/g, '').replace(/amp;/g, 'and'));
    this.fnParamMap = AnalyticsQueryString.buildQueryMap(fnParamsUrl);

    return this.fnParamMap[key];
  }

  getBrowsePageParam(key) {
    const val = this.getFnParamVal(key) || this.getUrlParam(key);

    let formatedParam;
    if (val) {
      // Replaces "|" by " - "
      formatedParam = val.replace(/\|/g, ' - ');

      // Multiple value field, separated by ";;"
      if (formatedParam.indexOf(';;') > -1) {
        formatedParam = formatedParam.replace(/;;/g, `|${key}:`);
        formatedParam = `${key}:${formatedParam}`;
      }
    }

    return formatedParam;
  }

  getBrowsePageParamFilter(key) {
    let formatedParam = this.getFnParamVal(key) || this.getUrlParam(key);
    if (formatedParam) {
      formatedParam = formatedParam.replace(/%20/g, ' ').replace(/%27/g, "'");
    }
    return formatedParam;
  }

  static getCMParameter(paramName, queryString) {
    return AnalyticsQueryString.getTrackingValue(`cm_${paramName}`, queryString) || AnalyticsQueryString.getTrackingValue(paramName, queryString);
  }

  // Basic Getters
  isRegistryMode() {
    return this.productModel.get('viewState').detail.flags.isRegistryMode;
  }

  getPageID() {
    return `PRODUCT: ${this.getProductName()} (${this.getProductID()})`;
  }

  getProductName() {
    const { detail } = this.productModel.get('product');
    if (detail.brand && detail.brand.name) {
      return `${detail.brand.name} ${detail.name}`;
    }
    return `${detail.name}`;
  }

  getProductID() {
    return this.productModel.get('product').id.toString();
  }

  getPageCategoryID() {
    const pseudoCat = this.getUrlParam('PseudoCat');
    let catID = this.getCategoryID();

    if (this.isRegistryMode()) {
      catID = `MWEDD_${this.getCategoryID()}`;
      if (this.getUrlParam('cm_kws_ls') || this.getFnParamVal('lskws')) {
        catID += '_onsite_search_recentsearch';
      }
    } else if (this.getFnParamVal('vs')) {
      catID = 'visual_search';
    } else if (this.getFnParamVal('prosContent') && this.getFnParamVal('proSource') === 'col') {
      catID = 'shoppable_content_collections';
    } else if (this.isDLP()) {
      catID = 'DLP';
    } else if (this.isFromSearchPage()) {
      catID = 'onsite_search';
      if (this.getUrlParam('cm_kws_ac') || this.getFnParamVal('ackws')) {
        catID += '_autocomplete';
      } else if (this.getUrlParam('cm_kws_ls') || this.getFnParamVal('lskws')) {
        catID += '_recentsearch';
      }
    } else if (pseudoCat) {
      catID = pseudoCat;
    }

    return catID;
  }

  getCategoryID() {
    // getUrlParam('CategoryID') is the category where the user came from ( like "Clearance" )
    // defaultCategoryId is the product parent category ( like "Dress" )
    return this.getUrlParam('CategoryID') || this.productModel.get('product').relationships.taxonomy.defaultCategoryId;
  }

  getRootCategoryId() {
    const { relationships: { taxonomy: { categories } } } = this.productModel.get('product');
    return categories && categories[0] && categories[0].id;
  }

  // TODO: update Regex after discovery team (Santosh) provides a fix for the bug when DLP is being identified as the page after a search.
  isDLP() {
    // Dynamic Landing Page
    return this.getUrlParam('isDlp');
  }

  static getPreviousPage() {
    return document.referrer;
  }

  isFromSearchPage() {
    return !!this.getKeywordSearch();
  }

  getKeywordSearch() {
    if (this.getUrlParam('cm_sp') === 'search-improvement-_-autocomplete-product-rec-_-n') {
      return this.getBrowsePageParamFilter('kws');
    }
    return this.getUrlParam('cm_kws') || this.getFnParamVal('kws') || this.getUrlParam('kws');
  }

  getCrossSell() {
    return this.getUrlParam('RPI') ? 'CrossSell' : '';
  }

  // Page View Tag
  getPageViewExploreAttributes(experiments = []) {
    const attr = [];

    attr[3] = experiments.join('|'); // Experiments available on the page
    // TODO: Confirm if we need this, if so we need to do backend changes to implement a URL parameter that will
    // only exist in case of redirects to PDP,
    // then we can consume this parameter in the UI code.
    // attr[9] = this.isPDPRedirectedFromSearch() ? 'Product Page' : ''; // Redirect type
    attr[15] = AttributesHelper.getInternationalCountry(); // International country
    attr[27] = this.isFromSearchPage() ? AttributesHelper.getMachineID() : undefined; // Machine ID
    attr[31] = AttributesHelper.getUserPCIDUserLoginID(); // User PC ID User Login ID
    attr[35] = AttributesHelper.getUserStatus();

    return _.rest(attr);
  }

  static getInternationalCountry() {
    const countryName = CookieUtil.getStringCookie(Cookies.shippingCountry);

    if (countryName && countryName !== 'US') {
      return `INTERNATIONAL_${countryName}`;
    }
    return undefined;
  }

  static getMachineID() {
    return Cookie.get(Cookies.machineId);
  }

  static getUserPCIDUserLoginID() {
    const mboxValue = Cookie.get(Cookies.mbox);
    const macysOnlineUidValue = Cookie.get(Cookies.userGuid);

    let attr = '';

    if (mboxValue) {
      const pcArray = mboxValue.split('PC#');
      let pcValue = pcArray[1];

      attr = 'PC#';

      if (pcValue) {
        if (pcValue.search('#') !== -1) {
          pcValue = pcValue.substr(0, pcValue.indexOf('#'));
        }
        attr += pcValue;
      }
    }

    if (Cookie.get(Cookies.signedIn) === '1' && macysOnlineUidValue) {
      attr = `${attr}|${macysOnlineUidValue}`;
    }

    return attr !== '' ? attr : undefined;
  }

  static getUserStatus() {
    // eslint-disable-next-line no-nested-ternary
    return UserStatus.isSoftSignedIn() ? 'Soft Signed-In' : UserStatus.isSignedIn() ? 'Signed-In' : 'Guest';
  }

  static getTierName() {
    const uid = Cookie.get(Cookies.userId);

    const tierName = 'NON-LOYALIST';

    const userInfo = localStorage.getItem(`hnfsr_${uid}`);

    if (!uid || !userInfo) return tierName;

    const { tierInfo } = JSON.parse(userInfo) || {};

    const userTierName = tierInfo && tierInfo.tierName;

    return userTierName || tierName;
  }

  // Product View Tag
  // getProductViewExploreAttributes() {
  //   const attr = [];
  //   const rvi = this.getRecentlyViewedItems();

  //   attr[1] = AttributesHelper.getProsPanel(); // PROS attributes, when coming from pros widget
  //   attr[4] = this.getBrowsePageParam('PRICE'); // Price Range Facet selected on Browse Page
  //   attr[5] = this.getBrowsePageParam('BRAND'); // Brands Facet selected on Browse Page
  //   attr[6] = this.getBrowseSizeFacet(); // Size Facet selected on Browse Page
  //   attr[7] = this.getAllOtherBrowseFacets(); // Parameters/Facets selected on Browse Page but not covered in other explore attributes
  //   attr[8] = this.getBrowsePageParam('COLOR'); // Color Facet selected on Browse Page
  //   attr[9] = this.getBrowsePageParam('spc'); // Number of product results on Browse Page
  //   attr[10] = this.getBrowsePageParam('sp'); // Page where product was selected on Browse Page
  //   // TODO: this info only comes if the user comes from MeW. Confirm if is that necessary since we are doing responsive.
  //   // attr[12] = this.getFromLocalStorage('togglemode'); // MeW Browse type
  //   attr[13] = this.getBigTicket(); // Big Ticket eligible item - TODO: please confirm flag after big ticket is implemented
  //   attr[15] = AttributesHelper.getInternationalCountry();
  //   attr[16] = this.getFullPageType(); // Full Page type: SINGLE ITEM, MEMBER | id, MASTER | id, CONSOLIDATED | id
  //   attr[17] = this.getStoreOnlyProduct(); // Store Only Product
  //   attr[18] = this.getUnavailableProduct(); // Unavailable Product
  //   attr[19] = this.getRdppTag(); // RuleId for product tracked from both Browse & Search pages.
  //   attr[20] = this.isFromSearchPage() ? undefined : this.getBrowsePageParam('slotId'); // Slot ID where product was selected on Browse Page
  //   attr[21] = this.getVideo(); // Product has video
  //   attr[22] = rvi.origin; // Recently Viewed Items Panel Type
  //   attr[23] = rvi.pos; // Recently Viewed Items thumbnail SlotID which was clicked on
  //   attr[27] = this.getSearchPageQuery() || this.getMoreLikeThisPageQuery(); // Onsite search
  //   attr[28] = this.isFromSearchPage() ? this.getBrowsePageParam('slotId') : undefined; // Slot ID where product was selected on Search Page
  //   attr[29] = this.getUrlParam('brandIndex') ? 'Shop by brand' : undefined; // Shop By Brand
  //   attr[30] = this.getPdpColorized(); // PDP Colorization
  //   attr[31] = this.getCountAlternateImages(); // number of alternate images
  //   attr[33] = this.getTruefit(); // True Fit eligible
  //   attr[34] = AttributesHelper.getStoreId();
  //   attr[42] = AttributesHelper.getBopsOnly(); // TODO: to be confirmed by Item Availability changes

  //   return _.rest(attr);
  // }

  getRecentlyViewedItems() {
    let rvi = this.getUrlParam('RVI');
    // eslint-disable-next-line no-mixed-operators
    rvi = rvi && rvi.split('_') || [];

    return {
      origin: rvi[0],
      pos: rvi[1],
    };
  }

  static getProsPanel() {
    const prosPos = AttributesHelper.getCMParameter('pos');
    const prosControlGroupId = AttributesHelper.getProsCGIDParameter();

    let attr;

    if (prosPos && prosControlGroupId) {
      const batchId = AttributesHelper.getProsBatchId();
      const headerId = AttributesHelper.getProsHeaderId();
      const sourceCatId = AttributesHelper.getCMParameter('srcCatID');

      const ids = [prosPos, prosControlGroupId];
      if (batchId) {
        ids.push(batchId);
      }
      if (headerId) {
        ids.push(headerId);
      }
      if (sourceCatId) {
        ids.push(sourceCatId);
      }

      attr = ids.join(' | ');
    }

    return attr;
  }

  static getProsCGIDParameter() {
    const choiceId = AttributesHelper.getCMParameter('choiceId');

    let cgId;
    if (choiceId && choiceId.split('-') && choiceId.split('-').length) {
      cgId = choiceId.split('-')[0].substring(3, 9);
    }

    return cgId || '';
  }

  static getProsBatchId() {
    const choiceId = AttributesHelper.getCMParameter('choiceId');

    let dId;
    if (choiceId) {
      // eslint-disable-next-line no-useless-escape
      dId = choiceId.match((/\-([\w\d\-]+)/));
    }

    return (dId && dId[1]) ? dId[1] : '';
  }

  static getProsHeaderId() {
    const choiceId = AttributesHelper.getCMParameter('choiceId');

    let hId;
    if (choiceId) {
      hId = choiceId.match(/@(H.+?)@/);
    }

    return (hId && hId.length) ? hId[1] : '';
  }

  getBrowseSizeFacet() {
    const sizeValue = this.getFnParamVal('SIZE');

    if (!(sizeValue === undefined)) {
      const piplineExp = new RegExp('\\|', 'g');

      let facetValues = sizeValue.split(';;');
      facetValues = facetValues.map((facetValue) => {
        const keyParam = facetValue.split(':')[0];
        return facetValue.replace(piplineExp, `|${keyParam}:`);
      });

      return facetValues.join('|').replace(/\s*,\s*/g, '_');
    }
    return this.getBrowsePageParam('SIZE');
  }

  getAllOtherBrowseFacets() {
    if (this.fnParamMap) {
      return _.chain(this.fnParamMap)
        .keys()
        .without('COLOR', 'BRAND', 'PRICE', 'SIZE', 'sp', 'spc', 'ruleId', 'slotId', 'kws', 'searchType', 'ackws', 'rdppSegmentId', 'searchPass', 'lskws')
        .map(key => `${key.replace(/_/g, ' ')}:${this.fnParamMap[key]}`)
        .value()
        .join('|') || undefined;
    }
    return undefined;
  }

  getBigTicket() {
    return this.productModel.get('product').detail.flags.bigTicketItem === true ? 'Big Ticket' : undefined;
  }

  getFullPageType() {
    const product = this.productModel.get('product');
    const productFlags = product.detail.flags;
    const viewFlags = this.productModel.get('viewState').detail.flags;
    const { masterProduct } = product.relationships;
    let pageType;

    if (productFlags.consolidatedProductComplex === true) {
      pageType = `CONSOLIDATED | ${masterProduct.id}`;
    } else if (productFlags.masterProduct === true || viewFlags.isMemberCard) {
      pageType = `MASTER | ${this.getUrlParam('ID')}`;
    } else if (productFlags.memberProduct === true) {
      const previousPageRegex = AttributesHelper.getPreviousPage().match(/\?ID=(\d*)/);
      const previousPageId = previousPageRegex && previousPageRegex[1];
      const referenceToMasterID = masterProduct && `${masterProduct.id}` === previousPageId ? ` | ${previousPageId}` : '';

      pageType = `MEMBER${referenceToMasterID}`;
    } else {
      pageType = 'SINGLE ITEM';
    }

    return pageType;
  }

  getStoreOnlyProduct() {
    return this.productModel.get('product').detail.flags.isStoreOnlyProductOnline ? 'store_only_product' : undefined;
  }
  getUnavailableProduct() {
    return this.productModel.get('product').availability.available ? undefined : 'Product Unavailable';
  }

  getRdppTag() {
    return this.getFnParamVal('ruleId') || decodeURIComponent(this.getUrlParam('ruleId') || '');
  }

  getVideo() {
    return this.productModel.get('product').imagery.videoId !== undefined ? 'ENHANCED_FEATURES_VIDEO' : undefined;
  }

  getMoreLikeThisPageQuery() {
    if (this.getMLTPageParam()) {
      return 'mlt_resp';
    }
    return null;
  }

  getSearchPageQuery() {
    const kws = this.getKeywordSearch();
    const kwsSp = this.getKeywordSearchSearchPass();
    const kwsAc = this.getKeywordSearchAutoComplete();
    const kwsLs = this.getKeywordSearchLastSearches();

    if (kws && kwsAc) {
      // fn=sp=1&spc=3104&slotId=1&kws=jeans&searchType=ac&ackws=jea
      // Autocomplete: jeans | ac: jea
      if (this.getUrlParam('cm_sp') === 'search-improvement-_-autocomplete-product-rec-_-n') {
        return `Autocomplete-product: ${kws} | ac: ${kwsAc}`;
      }
      return `Autocomplete: ${kws} | ac: ${kwsAc}`;
    } else if (kws && kwsLs) {
      // fn=sp=1&spc=3104&slotId=1&kws=jeans&searchType=ls&lskws=jeans
      // ls: jeans
      // return 'ls: ' + kwsLs + ( kwsSp ? ' | Search Pass:' + kwsSp : '' );
      return `Recent Search:${kwsLs} | Search Pass:${kwsSp}`;
    } else if (kws) {
      return kws + (kwsSp ? ` | Search Pass:${kwsSp}` : '');
    }
    return null;
  }

  getMLTPageParam() {
    return this.getBrowsePageParam('mlt');
  }

  getKeywordSearchAutoComplete() {
    if (this.getUrlParam('cm_sp') === 'search-improvement-_-autocomplete-product-rec-_-n') {
      return this.getBrowsePageParamFilter('ackws');
    }
    return this.getBrowsePageParam('ackws');
  }

  getKeywordSearchLastSearches() {
    return this.getBrowsePageParam('lskws');
  }

  getKeywordSearchSearchPass() {
    return this.getBrowsePageParam('searchPass');
  }

  getPdpColorized() {
    return this.productModel.get('product').detail.flags.PDPColorized === true ? 'PDP Colorized' : 'PDP Non Colorized';
  }

  getCountAlternateImages() {
    return this.productModel.get('product').detail.additionalImagesCount;
  }

  getTruefit() {
    return this.productModel.get('product').detail.flags.truefitEligible === true ? 'True Fit eligible' : '';
  }

  static getStoreId() {
    const storeLocNumber = CookieUtil.getNumberCookie(Cookies.bopsPickupInStore, Cookies.miscGCs);
    if (storeLocNumber) {
      return storeLocNumber;
    }
    return 'No Preferred Store';
  }

  // getBopsAvailability() {
  //   const selectedUpcId = this.productModel.get('viewState').detail.selectedUpc;

  //   if (selectedUpcId === undefined) {
  //     return undefined;
  //   }

  //   const selectedUpc = this.productModel.get('product').relationships.upcs[selectedUpcId];

  //   const bops = selectedUpc.availability.bopsAvailability ? 'Available' : 'Unavailable';
  //   const isPickUpSelected = this.productModel.get('viewState').detail.isPickUpInStoreSelected ? 'Selected' : 'Deferred';
  //   const isOnlineAvailable = AttributesHelper.getBopsAvailabilityForOnline(selectedUpc);
  //   return bops === 'Unavailable' ? `${isOnlineAvailable} | BOPS: ${bops}` : `${isOnlineAvailable} | BOPS: ${bops}-${isPickUpSelected}`;
  // }

  // getBossAvailability() {
  //   const selectedUpcId = this.productModel.get('viewState').detail.selectedUpc;

  //   if (selectedUpcId === undefined) {
  //     return undefined;
  //   }

  //   const selectedUpc = this.productModel.get('product').relationships.upcs[selectedUpcId];

  //   const boss = !selectedUpc.availability.bopsAvailability && selectedUpc.availability.bossAvailability ? 'Available' : 'Unavailable';
  //   const isPickUpSelected = this.productModel.get('viewState').detail.isPickUpInStoreSelected ? 'Selected' : 'Deferred';
  //   return boss === 'Unavailable' ? `BOSS: ${boss}` : `BOSS: ${boss}-${isPickUpSelected}`;
  // }

  // static getBopsAvailabilityForOnline(selectedUpc) {
  //   let online;

  //   if (selectedUpc.availability.available !== true) {
  //     online = 'Online: Unavailable';
  //   } else if (!_.isEmpty(selectedUpc.availability.lowAvailabilityMessage)) {
  //     online = 'Online: Low';
  //   } else {
  //     online = 'Online: In Stock';
  //   }

  //   return online;
  // }

  static getBopsOnly() {
    // TODO: to be confirmed by Item Availability changes
    // let attr;

    // if (!this.productModel.get('product').detail.flags.masterProduct) {
    //     const upcs = this.productModel.get('product').relationships.upcs;

    //     const isBopsOnly = _.every(upcs, upc => upc.availability.available === false && upc.availability.isAvailableForBOPS === true);
    //     const isNotBopsOnly = _.every(upcs, upc => upc.availability.available === true);

    //     if (isBopsOnly) {
    //         attr = 'BOPS_Only';
    //     } else if (isNotBopsOnly) {
    //         attr = 'No_BOPS_Only';
    //     } else {
    //         attr = 'Partially_BOPS_Only';
    //     }
    // }

    // return attr;
  }

  // Shop Action 5 Tag
  // getShop5ExploreAttributes(isQuickLook) {
  //   const attr = this.getProductViewExploreAttributes();
  //   attr.unshift('');

  //   attr[3] = this.productModel.get('cmData') && this.productModel.get('cmData').fullPageAddToBaginterstitialExperimentValue;
  //   attr[32] = this.productModel.get('viewState').detail.interactedAltImages; // Number of alternate images that have been interacted with
  //   attr[36] = this.getBigTicketDeliveryStatus(); // Delivery status for BigTicket order
  //   attr[40] = isQuickLook ? 'Quicklook' : undefined; // If the ATB action came from Quicklook
  //   attr[41] = this.getBopsAvailability(); // BOPS Availability
  //   attr[44] = this.getBossAvailability(); // BOSS Availability

  //   return _.rest(attr);
  // }

  // TODO: verify this is correct when implementing Big Ticket on PDP
  // getBigTicketDeliveryStatus() {
  //   return this.productModel.get('viewState').detail.bigTicketDeliverabilityStatus === 'BACKORDER' ? 'On Order' : undefined;
  // }
}



// WEBPACK FOOTER //
// /Users/m802941/WebstormProjects/2020/campaigns/shop-the-look-pdp/node_modules/@page/digital-product-ui-mcom/src/js/util/AttributesHelper.js